import axios from 'axios';
import {
  convertKeysToSnakeCase,
  convertKeysToCamelCase,
} from 'src/utils/formatUtils';
import store from 'src/redux/store';
import { setGlobalError } from 'src/redux/Actions/GlobalError';
import { logout } from 'src/redux/Actions/Auth';
import * as Sentry from '@sentry/react';
import config from 'src/config/config';

const api = axios.create();

function requestHandler(req) {
  const token = localStorage.getItem('token');

  // merge headers correctly
  const headers = {
    ...req.headers,
    'X-Frontend-Version': config.frontend_version,
  };

  if (token) {
    // Adjust this depending on your backend: "Token" or "Bearer"
    headers.Authorization = `Token ${token}`;
  }

  // Do not convert keys if no data
  const isJson =
    req.headers?.['Content-Type'] === 'application/json' ||
    req.headers?.['content-type'] === 'application/json';

  return {
    ...req,
    headers,
    data: isJson && req.data ? convertKeysToSnakeCase(req.data) : req.data,
    params: req.params ? convertKeysToSnakeCase(req.params) : req.params,
  };
}

function responseSuccessHandler(res) {
  return convertKeysToCamelCase(res.data);
}

function responseErrorHandler(error) {
  const status = error?.response?.status;

  if (status === 401) {
    store.dispatch(logout());

    // Treat as handled: don't reject → no unhandled rejection → not sent to Sentry
    return Promise.resolve(null); // or return a custom object
  }

  if (status === 403) {
    if (window.history.length > 1) return window.history.back();
    window.location.href = '/403';
    return Promise.resolve(null);
  }

  if (status === 404) {
    if (window.history.length > 1) return window.history.back();
    window.location.href = '/404';
    return Promise.resolve(null);
  }

  if (status === 400) {
    // Add breadcrumbs for debugging context
    Sentry.addBreadcrumb({
      category: 'api',
      message: `400 Bad Request - ${error.config?.url || 'Unknown URL'}`,
      level: 'warning',
      data: {
        request: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
          data: error.config?.data,
          params: error.config?.params,
        },
        response: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          headers: error.response?.headers,
          data: error.response?.data,
        },
      },
    });

    // Show error to user and reject promise
    store.dispatch(setGlobalError(error));
    return Promise.reject(error);
  }

  store.dispatch(setGlobalError(error));
  return Promise.reject(error);
}
api.interceptors.request.use(requestHandler);
api.interceptors.response.use(responseSuccessHandler, responseErrorHandler);

export default api;
